import { ChevronDown, LogOut } from "lucide-react";

import { useMe } from "@/lib/api/me";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// UserMenu is the header account dropdown: a ghost-button trigger showing the
// signed-in user's email + per-install app_role badge, opening to an org/role
// header and a Log out action. Modeled on the taco app's UserMenu. It only
// renders inside the authenticated shell (RootLayout), so it assumes a session
// exists; useMe() fills in the identity once /v1/me resolves.
//
// Log out is a full-page navigation to the backend's /auth/logout, which clears
// our session + presence cookies and chains through the auth service's logout
// (clearing its refresh cookie) before returning to the landing page — so the
// next visit shows the login form rather than silently re-authenticating.
export function UserMenu() {
  const me = useMe();

  // Label falls back email → org_name → "Account" so an authenticated user
  // without an email claim still gets a usable trigger.
  const chipLabel = me.data?.email || me.data?.org_name || "Account";
  const appRole = me.data?.app_role;
  const orgName = me.data?.org_name;
  const orgRole = me.data?.role;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-2 px-2 text-sm text-muted-foreground hover:text-foreground"
          aria-label="Account menu"
        >
          <span className="max-w-[12rem] truncate font-medium text-foreground">
            {chipLabel}
          </span>
          {appRole && (
            <span
              className="rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary"
              title={`Per-install role: ${appRole}`}
            >
              {appRole}
            </span>
          )}
          <ChevronDown className="h-4 w-4 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[12rem]">
        {(orgName || orgRole) && (
          <>
            <DropdownMenuLabel className="font-normal">
              {orgName && (
                <div className="text-sm font-medium text-foreground">
                  {orgName}
                </div>
              )}
              {orgRole && (
                <div className="text-xs text-muted-foreground">org {orgRole}</div>
              )}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem
          onClick={() => {
            window.location.assign("/auth/logout");
          }}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default UserMenu;
